import type { PropsWithChildren } from "react";
import React, { useContext, useEffect, useMemo, useRef, useCallback } from "react";
import {
  Icon,
  Box,
  Stack,
  useColorModeValue,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Portal,
  Tooltip,
  useBreakpointValue,
  Center,
  useToast,
} from "@chakra-ui/react";
import type { IconType } from "react-icons";
import { track } from "api/analytics";
import { useNavigate } from "react-router-dom";
import { ConversationContext } from "screens/thread/ConversationContext";
import { useCollectionKey, useEntitlementKey, useItemSelector, useProjectParams, useSidebarNavigation, useUserProfile } from "hooks";
import { SettingsProviderContext } from "screens/panels/settings/SettingsProvider";
import { TextOverflowTooltip } from "./TextOverflowTooltip";
import { IoEllipsisVertical } from "react-icons/io5";
import { ProjectDeleteButton } from "screens/collection/views/ProjectActions/ProjectDeleteButton";
import { OptionsMenuItem } from "screens/collection/components/OptionsMenuItem";
import { useAddToCharliContext } from "screens/panels/addToCharli/AddToCharliWizard/AddToCharliProvider";
import { MdDragIndicator } from "react-icons/md";
import { SharePortfolioButton } from "screens/collection/views/ProjectActions/SharePortfolioButton";
import { FiShare2 } from "react-icons/fi";
import { useWorkflowSchedulesByPortfolio } from "hooks/useWorkflowSchedules";
import { IoIosTimer } from "react-icons/io";
import { useDispatch } from "react-redux";
import { sendMessage } from "state/websocket/operations";
import type { RequestEntities } from "types/charliui";
import { useCollections } from "hooks/useCollections";
import { ToastMessageContent } from "screens/common/components";

interface Props {
  text?: string;
  tooltip?: string;
  icon?: IconType | React.ReactElement;
  iconSize?: string;
  screen?: string;
  routeParams?: string[];
  onClick?: () => void;
  analyticsEvent?: string;
  cssClasses?: string[];
  style?: React.CSSProperties;
  isSelected?: boolean;
  isDisabled?: boolean;
  isChildProject?: boolean;
  menuProjectId?: string;
  isStatic?: boolean;
}

export const SidebarButton = React.forwardRef<unknown, PropsWithChildren<Props>>(
  (props: PropsWithChildren<Props>, ref: React.Ref<unknown>) => {
    const {
      text,
      tooltip,
      routeParams,
      icon,
      iconSize = "1.2rem",
      screen,
      onClick,
      analyticsEvent,
      cssClasses,
      style,
      isSelected,
      isChildProject = false,
      menuProjectId,
      isStatic,
      children,
    } = props;
    const { projectFilter, projectId, parentRoute, isPortfolios } = useProjectParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToast();
    const collections = useCollections();
    const selectedButtonColor = useColorModeValue("charli.primaryBlue", "charli.primaryBlue");
    const buttonColor = useColorModeValue("charli.darkBlue", "gray.300");
    const { onConversationClose, setRequestEntities, setConversationId, setIsAnotherInputFocused, setShowConversationMessages } =
      useContext(ConversationContext);
    const { setIsHeaderTextEditable, setIsEditing, setIsSideNavigationOpen } = useContext(SettingsProviderContext);
    const { resetSelectedItems } = useItemSelector();
    const { setSelectedSidebar, selectedView } = useSidebarNavigation();
    const projectName = useCollectionKey(projectId, "name");
    const { onDeleteProjectModalOpen, onSharePortfolioModalOpen, setSharePortfolioId } = useContext(ConversationContext);
    const { onPortfolioSettingsOpen } = useAddToCharliContext();
    const [showActionMenu, setShowActionMenu] = React.useState(false);
    const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const hasShareEntitlement = useEntitlementKey("enable_share_portfolio");
    const { id: currentUserId } = useUserProfile();
    const currentSharedDetails = useCollectionKey(menuProjectId, "shareDetails");
    const collectionType = useCollectionKey(menuProjectId, "collectionType");

    const portfolioCollections = useMemo(
      () =>
        collections.filter(
          (collection) => collection.collectionType === "portfolio" && collection.id !== projectFilter && !collection.shareDetails
        ),
      [collections, projectFilter]
    );

    const isCurrentProjectShared = useMemo(() => {
      return currentSharedDetails?.ownerId === currentUserId;
    }, [currentSharedDetails?.ownerId, currentUserId]);

    // State to track the dragged project ID for reactive updates
    const [draggedProjectId, setDraggedProjectId] = React.useState<string | null>(null);

    // Monitor for drag operations and update the dragged project ID
    React.useEffect(() => {
      const checkDraggedProject = () => {
        const currentDraggedId = document.body.getAttribute("data-dragged-project-id");
        setDraggedProjectId(currentDraggedId);
      };

      checkDraggedProject();

      // Set up a MutationObserver to watch for changes to the data-dragged-project-id attribute
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (
            mutation.type === "attributes" &&
            (mutation.attributeName === "data-dragged-project-id" || mutation.attributeName === "class")
          ) {
            checkDraggedProject();
          }
        });
      });

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ["data-dragged-project-id", "class"],
      });

      return () => observer.disconnect();
    }, []);

    // Check if this portfolio already contains the dragged project
    const isProjectInPortfolio = useMemo(() => {
      if (!menuProjectId || !draggedProjectId) return false;

      const portfolio = portfolioCollections.find((collection) => collection.id === menuProjectId);
      return portfolio?.metadataIds?.includes(draggedProjectId) || false;
    }, [menuProjectId, portfolioCollections, draggedProjectId]);

    const workflowSchedule = useWorkflowSchedulesByPortfolio(menuProjectId || "");
    const workflowScheduleFrequency = useMemo(() => workflowSchedule[workflowSchedule.length - 1]?.frequency, [workflowSchedule]);

    // State for drag over highlighting
    const [isDragOver, setIsDragOver] = React.useState(false);

    // Function to handle direct project move
    const handleDirectMove = useCallback(
      (collectionId: string, conversationId: string, targetPortfolioId: string) => {
        const maybeCurrentProjectPortfolioId = isPortfolios ? projectFilter : undefined;
        const portfolioName = portfolioCollections.find((collection) => collection.id === targetPortfolioId)?.name;
        const projectName = portfolioCollections.find((collection) => collection.id === collectionId)?.name;

        const entities: RequestEntities = [
          { entity: "collection_id", value: collectionId },
          { entity: "to_portfolio_id", value: targetPortfolioId },
        ];

        if (maybeCurrentProjectPortfolioId) {
          entities.push({ entity: "from_portfolio_id", value: maybeCurrentProjectPortfolioId });
        }

        dispatch(
          sendMessage({
            conversationId,
            intent: "/move_project",
            entities,
          })
        );
        toast.closeAll();
        toast({
          render: ({ onClose }) => (
            <ToastMessageContent
              message={`I'll move the project ${projectName} over to the ${portfolioName} portfolio now. This will only take a second or two.`}
              onClose={onClose}
            />
          ),
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      },
      [isPortfolios, projectFilter, portfolioCollections, dispatch, toast]
    );

    useEffect(() => {
      setSelectedSidebar(projectName || projectFilter || parentRoute || "");
    }, [projectName, projectFilter, parentRoute, setSelectedSidebar, projectId]);

    useEffect(() => {
      if (menuButtonRef.current) {
        if (showActionMenu) {
          menuButtonRef.current.removeAttribute("inert");
        } else {
          menuButtonRef.current.setAttribute("inert", "");
        }
      }
    }, [showActionMenu]);

    const onClickHandler = () => {
      if (analyticsEvent) track(analyticsEvent);
      if (onClick) {
        onClick();
      } else if (screen) {
        const params = routeParams && routeParams.length > 0 ? `/${routeParams.join("/")}` : "";
        navigate(`/${screen}${params}`);
      }
      setRequestEntities([]);
      setConversationId("");
      onConversationClose();
      setShowConversationMessages(false);
      setIsHeaderTextEditable(false);
      setIsEditing(false);
      resetSelectedItems();
      setIsAnotherInputFocused(false);
      setIsSideNavigationOpen(false);
    };

    const getIsItemSelected = React.useCallback((): boolean => {
      if (isSelected) return true;
      if (projectFilter && menuProjectId && projectFilter.trim() === menuProjectId.trim()) return true;
      if (screen?.includes(selectedView)) return true;
      if (!projectFilter && text?.toLowerCase() === selectedView.toLowerCase()) return true;
      return false;
    }, [isSelected, projectFilter, menuProjectId, screen, selectedView, text]);

    const isItemSelected = getIsItemSelected();

    return (
      <>
        <Box
          position="relative"
          width="100%"
          cursor={screen ? "pointer" : "default"}
          borderRadius="none"
          borderWidth="0"
          backgroundColor={isDragOver ? "blue.50" : "unset"}
          border={isDragOver ? "2px dashed" : "2px solid transparent"}
          borderColor={isDragOver ? "blue.400" : "transparent"}
          _hover={{ backgroundColor: isDragOver ? "blue.100" : "unset", color: selectedButtonColor }}
          _active={{}}
          // Show dotted blue border for valid drop targets when dragging
          sx={{
            "body.project-tile-dragging &":
              isChildProject &&
              menuProjectId &&
              (!currentSharedDetails || currentSharedDetails.ownerId === currentUserId) &&
              !isProjectInPortfolio
                ? {
                    border: "2px dashed",
                    borderColor: "blue.300",
                    backgroundColor: "blue.25",
                  }
                : {},
            "body.project-tile-dragging &:hover":
              isChildProject &&
              menuProjectId &&
              (!currentSharedDetails || currentSharedDetails.ownerId === currentUserId) &&
              !isProjectInPortfolio
                ? {
                    borderColor: "blue.400",
                    backgroundColor: "blue.50",
                  }
                : {},
          }}
          onClick={onClickHandler}
          onMouseEnter={() => setShowActionMenu(true)}
          onMouseLeave={() => setShowActionMenu(false)}
          className={cssClasses?.join(" ")}
          style={style}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragOver(false);
            if (
              isChildProject &&
              menuProjectId &&
              (!currentSharedDetails || currentSharedDetails.ownerId === currentUserId) &&
              !isProjectInPortfolio
            ) {
              const draggedId = e.dataTransfer.getData("text/plain");
              const draggedData = e.dataTransfer.getData("application/json");

              if (draggedId && draggedData) {
                try {
                  const { collectionId, conversationId } = JSON.parse(draggedData);
                  if (collectionId !== menuProjectId) {
                    handleDirectMove(collectionId, conversationId, menuProjectId);
                  }
                } catch (error) {
                  console.error("Error parsing drag data:", error);
                }
              }
            }
          }}
          onDragOver={(e) => {
            if (
              isChildProject &&
              menuProjectId &&
              (!currentSharedDetails || currentSharedDetails.ownerId === currentUserId) &&
              !isProjectInPortfolio
            ) {
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setIsDragOver(true);
            }
          }}
          onDragEnter={(e) => {
            if (
              isChildProject &&
              menuProjectId &&
              (!currentSharedDetails || currentSharedDetails.ownerId === currentUserId) &&
              !isProjectInPortfolio
            ) {
              e.preventDefault();
              setIsDragOver(true);
            }
          }}
          onDragLeave={(e) => {
            if (isChildProject && menuProjectId) {
              // Only set to false if we're actually leaving the element
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX;
              const y = e.clientY;

              if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                setIsDragOver(false);
              }
            }
          }}>
          <Stack direction="row" align="center" spacing=".2rem" width="100%" justifyContent={"space-between"}>
            <Tooltip offset={[35, 15]} maxWidth={"11rem"} label={tooltip} aria-label={tooltip}>
              <Stack direction={"row"} spacing=".5rem" width="100%">
                {!isMobile && isChildProject && !isStatic && (
                  <Icon cursor={"grab"} opacity={showActionMenu ? 1 : 0} as={MdDragIndicator} boxSize="1.2rem" />
                )}
                {!isMobile && isChildProject && isStatic && <Box width="22px" />}
                {icon &&
                  (React.isValidElement(icon) ? (
                    React.cloneElement(icon as React.ReactElement, {
                      style: {
                        width: iconSize,
                        height: iconSize,
                        color: isItemSelected || showActionMenu ? selectedButtonColor : buttonColor,
                        ...((icon as React.ReactElement<{ style?: React.CSSProperties }>).props?.style || {}),
                      },
                    })
                  ) : (
                    <Icon
                      as={icon as IconType}
                      fontWeight={isItemSelected ? "bold" : "normal"}
                      color={isItemSelected || showActionMenu ? selectedButtonColor : buttonColor}
                      borderColor={isItemSelected ? selectedButtonColor : buttonColor}
                      boxSize={iconSize}
                    />
                  ))}
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width="100%">
                  {text && (
                    <TextOverflowTooltip
                      color={isDragOver || isItemSelected || showActionMenu ? selectedButtonColor : buttonColor}
                      style={{
                        fontWeight: isDragOver || isItemSelected ? "bold" : "normal",
                      }}
                      fontSize={isMobile ? "lg" : "sm"}
                      label={text}
                      breakWord={false}
                    />
                  )}
                  <Stack direction={"row"} spacing="5px">
                    {isCurrentProjectShared && (
                      <Center>
                        <Icon
                          as={FiShare2}
                          fontWeight={isItemSelected ? "bold" : "normal"}
                          color={isItemSelected || showActionMenu ? selectedButtonColor : buttonColor}
                          borderColor={isItemSelected ? selectedButtonColor : buttonColor}
                          boxSize={"10px"}
                        />
                      </Center>
                    )}
                    {workflowScheduleFrequency && workflowScheduleFrequency !== "one-time" && (
                      <Center>
                        <Icon
                          as={IoIosTimer}
                          fontWeight={"bold"}
                          color={isItemSelected || showActionMenu ? selectedButtonColor : buttonColor}
                          borderColor={isItemSelected ? selectedButtonColor : buttonColor}
                          boxSize={"13px"}
                        />
                      </Center>
                    )}
                  </Stack>
                </Stack>
              </Stack>
            </Tooltip>
            <Stack direction={"row"} spacing="5px">
              {children}
              {isChildProject && !isMobile && !isStatic && (
                <Menu isLazy>
                  {showActionMenu && (
                    <MenuButton
                      ref={menuButtonRef}
                      className="ch-sidebar-options-menu"
                      height={"1rem"}
                      width={"1.2rem"}
                      pr="10px"
                      _hover={{ color: "primary.default" }}
                      _active={{}}
                      variant="ghost"
                      as={IconButton}
                      icon={<Icon as={IoEllipsisVertical} _active={{}} _hover={{}} />}
                      onClick={(event) => event.stopPropagation()}
                      minWidth="0"
                    />
                  )}
                  <Portal>
                    <MenuList
                      minWidth={"unset"}
                      zIndex={10000}
                      onMouseEnter={() => setShowActionMenu(true)}
                      onMouseLeave={() => setShowActionMenu(false)}>
                      <OptionsMenuItem
                        menuLabel={`${collectionType === "portfolio" ? "Portfolio" : "Dashboard"} Settings`}
                        className="ch-open-project-settings"
                        onClick={() => {
                          setSharePortfolioId(menuProjectId);
                          onPortfolioSettingsOpen();
                        }}
                      />

                      {menuProjectId && (
                        <>
                          {collectionType === "portfolio" && hasShareEntitlement && (
                            <SharePortfolioButton projectId={menuProjectId} onClick={onSharePortfolioModalOpen} />
                          )}
                          <ProjectDeleteButton
                            label={`Delete ${collectionType === "portfolio" ? "Portfolio" : "Dashboard"}`}
                            className="ch-delete-portfolio-sidebar"
                            onOpenConfirmation={onDeleteProjectModalOpen}
                            projectId={menuProjectId}
                            projectType={"portfolio"}
                            isMenuItem
                            navigatePath={projectFilter === menuProjectId ? "/dashboard" : undefined}
                          />
                        </>
                      )}
                    </MenuList>
                  </Portal>
                </Menu>
              )}
            </Stack>
          </Stack>
        </Box>
      </>
    );
  }
);
